/** The vendors module's part of the vendor dashboard: the store's online / offline switch (vendor journey step 4). */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router";

import { vendorStoreOnline, vendorStoreRetrieve } from "@motolynk/api-client";
import { meKey, usePermissions } from "@motolynk/app-core";
import { useTranslation } from "@motolynk/i18n";
import { Card, ConfirmDialog, Label, Switch, toast } from "@motolynk/ui";

import { storeKeys } from "./store/keys";

export function StoreStatusTile() {
  const { t } = useTranslation("vendors");
  const { can } = usePermissions();
  const queryClient = useQueryClient();
  const [confirming, setConfirming] = useState(false);
  const store = useQuery({
    queryKey: storeKeys.store,
    queryFn: async () => (await vendorStoreRetrieve()).data,
  });
  const toggle = useMutation({
    mutationFn: async (online: boolean) => (await vendorStoreOnline({ is_online: online })).data,
    onSuccess: (updated) => {
      queryClient.setQueryData(storeKeys.store, updated);
      void queryClient.invalidateQueries({ queryKey: meKey("vendor") });
      toast.success(updated.is_online ? t("store.online.turnedOn") : t("store.online.turnedOff"));
    },
    onError: (error) => toast.error(error instanceof Error ? error.message : String(error)),
  });
  const online = store.data?.is_online ?? true;
  return (
    <Card className="flex flex-col gap-3 p-5">
      <div className="flex items-start justify-between gap-3">
        <Label
          htmlFor="dashboard-store-online"
          className="text-sm font-medium text-muted-foreground"
        >
          {t("store.dashboard.title")}
        </Label>
        <Switch
          id="dashboard-store-online"
          aria-label={t("store.online.label")}
          checked={online}
          disabled={!store.data || !can("store.edit") || toggle.isPending}
          onCheckedChange={(on) => (on ? toggle.mutate(true) : setConfirming(true))}
        />
      </div>
      <p className="text-2xl font-bold">{store.data?.store_name ?? "—"}</p>
      <p className="text-sm text-muted-foreground">
        {online ? t("store.dashboard.online") : t("store.dashboard.offline")}{" "}
        <Link to="/store" className="font-medium text-foreground hover:underline">
          {t("store.dashboard.settings")}
        </Link>
      </p>
      <ConfirmDialog
        open={confirming}
        onOpenChange={setConfirming}
        title={t("store.online.confirmOffTitle")}
        description={t("store.online.confirmOffBody")}
        confirmLabel={t("store.online.confirmOff")}
        cancelLabel={t("actions.cancel")}
        tone="danger"
        onConfirm={() => toggle.mutateAsync(false)}
      />
    </Card>
  );
}
